import jsPDF from "jspdf";
import {
  formatCurrencyPdf,
  formatReceiptDate,
  stripHtmlToText,
} from "./formatters";

const MARGIN = 16;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const VALUE_X = 58;
const VALUE_W = PAGE_WIDTH - MARGIN - VALUE_X;
const FOOTER_RESERVE = 18;
const LOGO_PATH = "/assets/img/flowersvg.svg";

const svgToPngDataUrl = (svgUrl, widthPx = 100, heightPx = 90) =>
  new Promise((resolve) => {
    fetch(svgUrl)
      .then((res) => (res.ok ? res.text() : null))
      .then((svgText) => {
        if (!svgText) {
          resolve(null);
          return;
        }
        const objectUrl = URL.createObjectURL(
          new Blob([svgText], { type: "image/svg+xml;charset=utf-8" })
        );
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = widthPx * 3;
          canvas.height = heightPx * 3;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
            return;
          }
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(objectUrl);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
        img.src = objectUrl;
      })
      .catch(() => resolve(null));
  });

/** @param {import('jspdf').jsPDF} doc */
const resetTextStyle = (doc) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  if (typeof doc.setCharSpace === "function") {
    doc.setCharSpace(0);
  }
};

/** @param {import('jspdf').jsPDF} doc */
const ensureSpace = (doc, y, need = 8) => {
  if (y + need > PAGE_HEIGHT - FOOTER_RESERVE) {
    doc.addPage();
    return MARGIN;
  }
  return y;
};

/** @param {import('jspdf').jsPDF} doc */
const drawSection = (doc, y, title) => {
  y = ensureSpace(doc, y, 10);
  resetTextStyle(doc);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(title, MARGIN, y);
  y += 4;
  doc.setDrawColor(210, 210, 210);
  doc.setLineWidth(0.15);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  return y + 4;
};

/** @param {import('jspdf').jsPDF} doc */
const drawRows = (doc, y, rows) => {
  rows.forEach(([label, rawValue]) => {
    const value = stripHtmlToText(rawValue).replace(/\s+/g, " ").trim() || "—";

    y = ensureSpace(doc, y, 6);
    resetTextStyle(doc);
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, MARGIN, y);
    doc.setFont("helvetica", "normal");
    const valueLines = doc.splitTextToSize(value, VALUE_W);
    doc.text(valueLines, VALUE_X, y);
    y += Math.max(5, valueLines.length * 4.2) + 1;
  });
  return y;
};

/** @param {import('jspdf').jsPDF} doc */
const drawFooter = (doc, y) => {
  y = ensureSpace(doc, y, FOOTER_RESERVE);
  y += 4;
  doc.setDrawColor(220, 220, 220);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 5;
  resetTextStyle(doc);
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(
    "This is a system-generated receipt. For queries, contact Zenosure support.",
    MARGIN,
    y,
    { maxWidth: PAGE_WIDTH - MARGIN * 2 }
  );
  doc.text("www.zenosure.com", MARGIN, y + 4);
};

/**
 * Plain payment receipt PDF.
 * @param {object} receipt
 */
export const generateReceiptPdf = async (receipt) => {
  if (!receipt?.receiptNumber) {
    throw new Error("Invalid receipt data");
  }

  const logoPng = await svgToPngDataUrl(
    `${process.env.PUBLIC_URL || ""}${LOGO_PATH}`
  );

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const logoSize = 11;
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(220, 220, 220);
  doc.circle(MARGIN + logoSize / 2, y + logoSize / 2, logoSize / 2, "FD");

  if (logoPng) {
    try {
      doc.addImage(
        logoPng,
        "PNG",
        MARGIN + 1.2,
        y + 1.2,
        logoSize - 2.4,
        logoSize - 2.4
      );
    } catch {
      /* ignore */
    }
  }

  resetTextStyle(doc);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("Zenosure", MARGIN + logoSize + 3, y + 7);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("PAID", PAGE_WIDTH - MARGIN, y + 7, { align: "right" });

  y += logoSize + 4;
  doc.setFontSize(8);
  doc.setTextColor(70, 70, 70);
  doc.text(receipt.company?.name || "ZENOSURE DIGITAL PRIVATE LIMITED", MARGIN, y);
  y += 3.5;
  doc.text(`CIN: ${receipt.company?.cin || ""}`, MARGIN, y);
  y += 3.5;
  const addr = doc.splitTextToSize(
    receipt.company?.address || "",
    PAGE_WIDTH - MARGIN * 2
  );
  doc.text(addr, MARGIN, y);
  y += addr.length * 3.5 + 4;

  doc.setDrawColor(190, 190, 190);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 7;

  resetTextStyle(doc);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Payment Receipt", MARGIN, y);
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Receipt No: ${receipt.receiptNumber}`, MARGIN, y);
  doc.text(
    `Date: ${formatReceiptDate(receipt.generatedAt)}`,
    PAGE_WIDTH - MARGIN,
    y,
    { align: "right" }
  );
  y += 7;

  y = drawSection(doc, y, "Customer Details");
  const customerRows = [
    ["Name", receipt.user?.fullName],
    ["Email", receipt.user?.email],
    ["Mobile", receipt.user?.mobile],
    ["Address", receipt.user?.address],
  ];
  if (receipt.user?.gstin) {
    customerRows.push(["GSTIN", receipt.user.gstin]);
  }
  if (receipt.user?.pan) {
    customerRows.push(["PAN", receipt.user.pan]);
  }
  y = drawRows(doc, y, customerRows);

  y = drawSection(doc, y, "Plan Details");
  y = drawRows(doc, y, [
    ["Plan Name", receipt.plan?.name],
    ["Plan Type", receipt.plan?.type],
    ["Duration", receipt.plan?.duration],
    ["Valid From", formatReceiptDate(receipt.plan?.startDate, "DD MMM YYYY")],
    ["Valid To", formatReceiptDate(receipt.plan?.endDate, "DD MMM YYYY")],
    ["Enrollment Date", formatReceiptDate(receipt.enrollment?.enrollmentDate, "DD MMM YYYY")],
    ["Status", receipt.enrollment?.approvalStatus],
  ]);

  y = drawSection(doc, y, "Payment Details");
  y = drawRows(doc, y, [
    ["Transaction ID", receipt.payment?.transactionId],
    ["Payment Date", formatReceiptDate(receipt.payment?.paymentDate, "DD MMM YYYY, hh:mm A")],
    ["Payment Type", receipt.payment?.paymentType],
  ]);

  y = drawSection(doc, y, "Amount");
  const gst = receipt.payment?.gstBreakup;
  const amounts = [["Premium", formatCurrencyPdf(receipt.plan?.premiumAmount)]];

  if (gst?.cgst_amount != null) {
    amounts.push([`CGST (${gst.cgst_rate}%)`, formatCurrencyPdf(gst.cgst_amount)]);
    amounts.push([`SGST (${gst.sgst_rate}%)`, formatCurrencyPdf(gst.sgst_amount)]);
  } else if (gst?.igst_amount != null) {
    amounts.push([
      `IGST (${gst.igst_rate ?? 18}%)`,
      formatCurrencyPdf(gst.igst_amount),
    ]);
  } else if (receipt.plan?.gstAmount > 0) {
    amounts.push(["GST", formatCurrencyPdf(receipt.plan?.gstAmount)]);
  }

  amounts.forEach(([label, amt]) => {
    y = ensureSpace(doc, y, 5);
    resetTextStyle(doc);
    doc.text(label, MARGIN, y);
    doc.text(amt, PAGE_WIDTH - MARGIN, y, { align: "right" });
    y += 4.5;
  });

  y = ensureSpace(doc, y, 7);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Total Paid", MARGIN, y);
  doc.text(
    formatCurrencyPdf(receipt.payment?.paidAmount ?? receipt.plan?.totalAmount),
    PAGE_WIDTH - MARGIN,
    y,
    { align: "right" }
  );

  drawFooter(doc, y + 6);

  doc.save(`Receipt_${receipt.receiptNumber}.pdf`);
};
